import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Link,
  Grid,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  Stack,
  styled,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { Footer } from '@allenai/varnish2/components';

import {
  AI2CopyrightLink,
  FooterLinkSeparator,
  PrivacyLink,
  TermsOfUseLink,
} from '@allenai/varnish2';

import { fetchScenes } from '@/api/Scene';
import type { Scene } from '@/types/scene.types';
import { AnimatedCard } from '@/components/AnimatedCard';
import Logo from '@/components/Logo';
import { THOR_COLORS } from '@/theme/thorTheme';

import DownloadTxtLink from '@/components/LicenseDownload';

// Styled wrapper to reduce Footer padding
const FooterWrapper = styled(Box)(({ theme }) => ({
  '& > footer': {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(0.5),
  },
}));

const AnimatedSceneCard = ({ scene, onClick }: { scene: Scene; onClick: () => void }) => {
  return (
    <AnimatedCard
      key={scene.id}
      imageUrl={scene.thumbnailUrl}
      imageAlt={scene.name}
      title={scene.name}
      description={scene.description}
      videoUrl={scene.videoUrl}
      onClick={onClick}
    />
  );
};

export const Environment = () => {
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [scenesLoading, setScenesLoading] = useState(true);
  const [showAllScenes, setShowAllScenes] = useState(false);
  const [allScenes, setAllScenes] = useState<Scene[]>([]);
  const [allScenesLoading, setAllScenesLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  // Check if feature flag is enabled via URL parameter
  const showAllScenesFeature = searchParams.get('showAllScenes') === 'true';

  useEffect(() => {
    setScenesLoading(true);
    fetchScenes()
      .then((allScenes) => {
        // Only show scenes with custom thumbnails on first load
        const featuredScenes = allScenes.filter((scene) => scene.featured);
        setScenes(featuredScenes);
      })
      .catch((err: unknown) => {
        console.error('Failed to fetch scenes:', err);
      })
      .finally(() => {
        setScenesLoading(false);
      });
  }, []);

  const handleShowAllEnvironments = async () => {
    if (showAllScenes) {
      // Toggle off
      setShowAllScenes(false);
      return;
    }

    // Toggle on and fetch if not already fetched
    setShowAllScenes(true);
    if (allScenes.length === 0) {
      setAllScenesLoading(true);
      try {
        // Fetch all scenes from GCS (auto-discovered)
        const discoveredScenes = await fetchScenes();
        // Sort: scenes with thumbnails first, then the rest
        const sorted = discoveredScenes.sort((a, b) => {
          const aHasThumb = a.thumbnailUrl && !a.thumbnailUrl.includes('default_thumb');
          const bHasThumb = b.thumbnailUrl && !b.thumbnailUrl.includes('default_thumb');

          if (aHasThumb && !bHasThumb) return -1;
          if (!aHasThumb && bHasThumb) return 1;
          return a.name.localeCompare(b.name);
        });

        setAllScenes(sorted);
      } catch (err: unknown) {
        console.error('Failed to fetch all scenes:', err);
      } finally {
        setAllScenesLoading(false);
      }
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        minHeight: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: '1280px',
        margin: '0 auto',
        px: 3,
        pb: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          pt: 4,
        }}
      >
        <Stack
          direction="row"
          sx={{
            mb: 4,
          }}
        >
          <Logo clickable={false} />
        </Stack>
        <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'center',
              gap: 1.5,
              mb: 4,
            }}
          >
            {(() => {
              const pinkFill = `%23${THOR_COLORS.pink.slice(1)}`;
              const svg = (label: string, w: number) =>
                `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='36'><rect width='${w}' height='36' rx='18' fill='${pinkFill}'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='DejaVu Sans,sans-serif' font-size='13' fill='white'>${label}</text></svg>`;
              return (
                <>
                  <a href="http://allenai.org/papers/molmospaces" target="_blank" rel="noopener noreferrer">
                    <img alt="Paper" src={svg('📄 Paper', 100)} />
                  </a>
                  <a href="https://github.com/allenai/molmospaces" target="_blank" rel="noopener noreferrer">
                    <img alt="Code" src={svg('💻 Code', 100)} />
                  </a>
                  <a href="https://huggingface.co/datasets/allenai/molmospaces" target="_blank" rel="noopener noreferrer">
                    <img alt="Data" src={svg('🤗 Data', 100)} />
                  </a>
                  <a href="https://molmospaces.allen.ai/" target="_blank" rel="noopener noreferrer">
                    <img alt="Demo" src={svg('🚀 Demo', 100)} />
                  </a>
                  <a href="https://molmospaces.allen.ai/leaderboard" target="_blank" rel="noopener noreferrer">
                    <img alt="Leaderboard" src={svg('🏆 Leaderboard', 140)} />
                  </a>
                </>
              );
            })()}
          </Box>

          <Typography variant="body1" sx={{ mb: 1.5, maxWidth: '800px', whiteSpace: 'pre-line' }}>
            This demo hosts a snapshot of the 230K environments and 130K objects available in{' '}
            {
              <Box component="span" fontWeight="bold" display="inline">
                MolmoSpaces
              </Box>
            }
            . Explore some example interactions within these spaces by selecting one below.
            <Box component="span" sx={{ wordBreak: 'break-word', mb: 1, display: 'block' }}>
              {/* {'\n'} */}
            </Box>
            Visit our{' '}
            <Link
              href="https://allenai.org/embodied-ai"
              target="_blank"
              sx={{ color: THOR_COLORS.cream }}
            >
              Embodied AI
            </Link>{' '}
            to learn more about these environments, objects, object grasp dataset, embodied tasks,
            benchmarking, and more. See how different policies compare on our{' '}
            <Link
              href="/leaderboard"
              onClick={(e) => {
                e.preventDefault();
                navigate('/leaderboard');
              }}
              sx={{ color: THOR_COLORS.cream }}
            >
              Benchmark Leaderboard
            </Link>
            .
          </Typography>
          {isSmallScreen && (
            <Typography
              variant="body1"
              sx={{ mb: 4, fontWeight: 'bold', backgroundColor: 'rgba(15, 203, 140, 0.48)' }}
            >
              This demo isn&apos;t optimized for mobile. Try a desktop environment for the optimal
              experience.
            </Typography>
          )}

          <Typography variant="h3" sx={{ mb: 2.5 }}>
            Select an environment
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, maxWidth: '800px' }}>
            Choose from 5 diverse environments to explore. Each environment offers unique robotics
            challenges.
          </Typography>

          {scenesLoading ? (
            <Typography variant="body1" color="text.secondary">
              Loading scenes...
            </Typography>
          ) : scenes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No scenes found
            </Typography>
          ) : (
            <Grid
              container
              spacing={3}
              justifyContent="center"
              sx={{ maxWidth: '1400px', px: 3, width: '100%' }}
            >
              {scenes.map((scene) => (
                <Grid key={scene.id} size={{ xs: 12, sm: 6, md: 4 }} display="flex">
                  <AnimatedSceneCard
                    scene={scene}
                    onClick={() => {
                      navigate(`/scene/${scene.slug}`);
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Feature flag: Show "See all environments" button when ?showAllScenes=true */}
          {showAllScenesFeature && (
            <Button
              variant="contained"
              onClick={handleShowAllEnvironments}
              sx={{ mt: 4, mb: showAllScenes ? 2 : 0 }}
            >
              {showAllScenes ? 'Hide all environments' : 'See all environments'}
            </Button>
          )}

          {/* All Environments List (shown inline when toggled) */}
          {showAllScenes && (
            <Box sx={{ width: '100%', maxWidth: '800px', mt: 4, mb: 0 }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                All Available Environments
              </Typography>

              {allScenesLoading ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ py: 4, textAlign: 'center' }}
                >
                  Loading all environments from GCS...
                </Typography>
              ) : allScenes.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 4, textAlign: 'center' }}
                >
                  No environments found
                </Typography>
              ) : (
                <List
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  {allScenes.map((scene, index) => (
                    <ListItem key={scene.id} disablePadding divider={index < allScenes.length - 1}>
                      <ListItemButton
                        onClick={() => {
                          const url = `/test?scene=${scene.id}`;
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 2,
                          color: THOR_COLORS.cream,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemText
                          primary={scene.name}
                          secondary={scene.description || scene.slug}
                          slotProps={{
                            primary: {
                              variant: 'body1',
                              fontWeight: 500,
                              color: THOR_COLORS.cream,
                            },
                            secondary: {
                              sx: {
                                color: THOR_COLORS.cream,
                                opacity: 0.7,
                              },
                            },
                          }}
                        />
                        <OpenInNewIcon
                          fontSize="small"
                          sx={{ color: THOR_COLORS.cream, opacity: 0.7 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </Box>
        <FooterWrapper sx={{ marginTop: 'auto', width: '100%', pt: 4 }}>
          <Footer variant="default">
            <AI2CopyrightLink />
            <FooterLinkSeparator />
            <DownloadTxtLink href="/files/attribution.txt" color="secondary">
              Asset Attribution
            </DownloadTxtLink>
            <FooterLinkSeparator />
            <PrivacyLink />
            <FooterLinkSeparator />

            <TermsOfUseLink />
            <FooterLinkSeparator />

            <Link
              color="secondary"
              href="https://allenai.org/responsible-use"
              target="_blank"
              rel="noopener noreferrer"
            >
              Responsible Use
            </Link>
          </Footer>
        </FooterWrapper>
      </Box>
      );
};
